import { SortOrder } from 'enums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Filter, Originator, Params } from 'types';
import fetch from 'utils/fetch';
import { getOriginatorDetail, postOriginators } from './register-api';

import { GridParams } from './register-types';

const NOOP = () => {};
const ROWS_PER_PAGE_OPTIONS = ['5', '10', '25'];

/**
 * function takes originator class and call the endpoint whicch returns first letters of all the originators with the class
 * because the endpoint is async the function returns also class for which the letters were searched
 *
 * @param originatorClass - an originator class
 */
export function useRegisterLetters(originatorClass?: string) {
  const [letters, setLetters] = useState<string[]>([]);
  const [forClass, setForClass] = useState<string | undefined>();

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await fetch('/api/originator/first-characters', {
          method: 'GET',
          params:
            !originatorClass || originatorClass === 'all'
              ? {}
              : {
                  class: originatorClass.toLocaleUpperCase(),
                },
        });
        const json = await response.json();

        setLetters(json);
        setForClass(originatorClass);
      } catch (e) {
        setLetters([]);
        setForClass('');
      }
    };

    getData();
  }, [originatorClass]);

  return { letters, forClass };
}

const foldedVariants: Record<string, string[]> = {
  C: ['Č', 'č', 'Ch', 'ch'],
  R: ['Ř', 'ř'],
  S: ['Š', 'š'],
  Z: ['Ž', 'ž'],
};

const foldedVariantsWithDiacritics: Record<string, string[]> = {
  Č: ['č'],
  Ř: ['ř'],
  Š: ['š'],
  Ž: ['ž'],
  Ch: ['CH', 'Ch', 'ch'],
};

const filterWithFoldedVariants = (letter: string): boolean =>
  ['C', 'R', 'S', 'Z'].findIndex((l) => l === letter) !== -1;

const applyDisabledFolding = (letter: string): boolean =>
  ['Č', 'Ř', 'Š', 'Ž', 'Ch'].findIndex((l) => l === letter) !== -1;

const getFoldedFilter = (letter: string) => {
  const filters: Filter[] = [];
  const field = 'preferredName.label';
  const operation = 'START_WITH';
  if (filterWithFoldedVariants(letter)) {
    filters.push({
      operation: 'AND',
      filters: [
        {
          field,
          operation,
          value: letter,
        },
        ...foldedVariants[letter].map((value) => ({
          operation: 'NOT',
          filters: [
            {
              field,
              useFolding: false,
              operation,
              value,
            },
          ],
        })),
      ],
    });
  } else {
    if (applyDisabledFolding(letter)) {
      filters.push({
        operation: 'OR',
        filters: [
          {
            field,
            operation,
            useFolding: false,
            value: letter,
          },
          ...foldedVariantsWithDiacritics[letter].map((value) => ({
            field,
            operation,
            value,
            useFolding: false,
          })),
        ],
      });
    } else {
      filters.push({
        field,
        operation,
        value: letter,
      });
    }
  }
  return filters;
};

/**
 * modified table hook for a Table component
 *
 * @param search - searched text
 */
export function useTableHook() {
  const [order] = useState<SortOrder>(SortOrder.ASC);
  const [orderBy] = useState<string>('preferredName.label');
  const { push } = useHistory();
  const {
    search,
    originatorClass,
    page: rawPage,
    rowsPerPage: rawRowsPerPage,
  } = useParams<GridParams>();
  const decodedSearch = decodeURIComponent(search ?? '');

  const [rows, setRows] = useState<Originator[]>([]);
  const [count, setCount] = useState(0);

  const handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => void = useCallback(
    (_, page) => {
      push(
        ['/rejstriky', originatorClass, decodedSearch, page, rawRowsPerPage]
          .filter((att) => att !== undefined)
          .join('/')
      );
    },
    [decodedSearch, originatorClass, push, rawRowsPerPage]
  );

  const handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void = useCallback(
    (event) => {
      const rowsPerPage = event.target.value;

      push(
        ['/rejstriky', originatorClass, decodedSearch, 0, rowsPerPage]
          .filter((att) => att !== undefined)
          .join('/')
      );
    },
    [decodedSearch, originatorClass, push]
  );

  useEffect(() => {
    const allParams = [
      originatorClass,
      decodedSearch,
      rawPage,
      rawRowsPerPage,
    ].every((p) => p !== undefined);

    if (allParams) {
      const page = +rawPage!;
      const rowsPerPage = +rawRowsPerPage!;
      const foldedFilters = getFoldedFilter(decodedSearch!);
      const filters: Filter[] = [...foldedFilters];
      if (originatorClass !== 'all') {
        filters.push({
          field: 'originatorClass',
          operation: 'EQ',
          value: originatorClass?.toLocaleUpperCase(),
        });
      }

      const params: Params = {
        sort: [{ field: orderBy, order }],
        filters,
        offset: page * rowsPerPage,
        size: rowsPerPage,
      };

      const getTableItems = async () => {
        try {
          const { items, count } = await postOriginators(params);

          if (items && count !== undefined) {
            setCount(count);
            setRows(items);
          }
        } catch (e) {
          console.log(e);
        }
      };

      getTableItems();
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedSearch, order, orderBy, originatorClass, rawPage, rawRowsPerPage]);

  const returnValue = useMemo(
    () => ({
      page: rawPage ? +rawPage! : 0,
      rowsPerPage: rawRowsPerPage ? +rawRowsPerPage! : 10,
      order,
      orderBy,
      rows,
      count,
      handleChangePage,
      handleChangeRowsPerPage,

      // not used dummy data because of type
      setCount: NOOP,
      setOrder: NOOP,
      setOrderBy: NOOP,
      setRows: NOOP,
      setPage: NOOP,
      setRowsPerPage: NOOP,
    }),
    [
      count,
      handleChangePage,
      handleChangeRowsPerPage,
      order,
      orderBy,
      rawPage,
      rawRowsPerPage,
      rows,
    ]
  );
  return returnValue;
}

/**
 * register hook which mainly looks after correct url
 *
 */
export function useRegister(detailView: boolean) {
  const { originatorClass, search, page, rowsPerPage, id } = useParams<
    GridParams
  >();
  const decodedSearch = decodeURIComponent(search ?? '');

  const { replace } = useHistory();
  const { letters, forClass } = useRegisterLetters(originatorClass);

  // Search handlers
  const handleSearchSet = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 0) {
        replace(
          `/rejstriky/${originatorClass}/${encodeURIComponent(newSearch!)}`
        );
      }
    },
    [originatorClass, replace]
  );
  const handleSearchRemove = useCallback(() => {
    const firstLetter = !!decodedSearch
      ? decodedSearch.charAt(0).toUpperCase()
      : undefined;

    const correctLetter = letters.some((l) => l === firstLetter);

    if (correctLetter) {
      replace(
        `/rejstriky/${originatorClass}/${encodeURIComponent(firstLetter!)}`
      );
    } else {
      replace(`/rejstriky/${originatorClass}`);
    }
  }, [decodedSearch, letters, originatorClass, replace]);

  // Detail handler
  const handleDetailId = (eid: string) => {
    if (eid === id) {
      replace(
        ['/rejstriky', originatorClass, search, page, rowsPerPage]
          .filter((att) => att !== undefined)
          .join('/')
      );
    } else {
      replace(
        ['/rejstriky', originatorClass, search, page, rowsPerPage, eid]
          .filter((att) => att !== undefined)
          .join('/')
      );
    }
  };

  // Effect that looks after correct url
  useEffect(() => {
    if (detailView) {
      return;
    }

    const allParams = [originatorClass, decodedSearch, page, rowsPerPage].every(
      (att) => att !== undefined
    );

    // missing key params and we have correct letters that are not empty
    const keyParamMissing =
      !allParams && forClass === originatorClass && letters.length > 0;

    // wrong rowsPerPage
    const wrongRowsPerPage = ROWS_PER_PAGE_OPTIONS.every(
      (r) => r !== rowsPerPage
    );

    if (keyParamMissing || wrongRowsPerPage) {
      const newClass = originatorClass ?? 'all';
      const newSearch =
        forClass === originatorClass
          ? !!decodedSearch
            ? decodedSearch
            : letters?.[0]
          : undefined;
      const newPage = newSearch ? page ?? '0' : undefined;
      const newRowsPerPage = newPage
        ? wrongRowsPerPage
          ? '10'
          : rowsPerPage
        : undefined;
      const newId = !!newPage && newPage === page ? id : undefined;

      const newUrl = [
        '/rejstriky',
        newClass,
        newSearch,
        newPage,
        newRowsPerPage,
        newId,
      ]
        .filter((att) => att !== undefined)
        .join('/');

      replace(newUrl);
    }
  }, [
    forClass,
    id,
    letters,
    originatorClass,
    page,
    replace,
    rowsPerPage,
    decodedSearch,
    detailView,
  ]);

  return {
    letters,
    detailId: id,
    handleDetailId,
    handleSearchSet,
    handleSearchRemove,
  };
}

/**
 * register detail hook for getting detail info
 *
 */
export function useRegisterDetail() {
  const { id } = useParams<GridParams>();
  const [data, setData] = useState<Originator | undefined>(undefined);

  useEffect(() => {
    if (!!id) {
      const getData = async () => {
        const newData = await getOriginatorDetail(id);
        setData(newData);
      };
      getData();
    } else {
      setData(undefined);
    }
  }, [id]);

  return data;
}

export const processRelations = () => {
  return [];
};
